import ChatGPTAdMockup from "../../components/ChatGPTAdMockup";
import ChatGPTAdsCampaignWizardMockup from "../../components/ChatGPTAdsCampaignWizardMockup";
import ChatGPTAdsContextHintsMockup from "../../components/ChatGPTAdsContextHintsMockup";
import ChatGPTAdsBudgetEstimator from "../../components/ChatGPTAdsBudgetEstimator";
import AIAnalyticsOverviewMockup from "../../components/AIAnalyticsOverviewMockup";
import FAQItems from "../components/FAQItems";
import ScreenshotFrame from "../../components/ScreenshotFrame";

const TEXT = "#EDEDED";
const TEXT_DIM = "#a0a0a0";
const TEAL = "#4FCDC4";
const TEAL_DIM = "rgba(79,205,196,0.15)";
const CARD_BORDER = "rgba(255,255,255,0.04)";

function H2({ id, children }) {
  return (
    <h2
      id={id}
      style={{
        fontFamily: "var(--display)",
        fontWeight: 700,
        fontSize: "clamp(22px, 3vw, 28px)",
        color: TEXT,
        margin: "48px 0 16px",
        letterSpacing: "-0.01em",
        lineHeight: 1.2,
      }}
    >
      {children}
    </h2>
  );
}

function H3({ children }) {
  return (
    <h3
      style={{
        fontFamily: "var(--display)",
        fontWeight: 600,
        fontSize: "clamp(18px, 2.5vw, 22px)",
        color: TEXT,
        margin: "32px 0 12px",
        letterSpacing: "-0.01em",
        lineHeight: 1.3,
      }}
    >
      {children}
    </h3>
  );
}

function P({ children }) {
  return (
    <p style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.85, margin: "0 0 18px" }}>
      {children}
    </p>
  );
}

function Li({ children }) {
  return (
    <li style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.8, marginBottom: 8 }}>
      {children}
    </li>
  );
}

function Strong({ children }) {
  return <strong style={{ color: TEXT, fontWeight: 700 }}>{children}</strong>;
}

function A({ href, children }) {
  const isExternal = href.startsWith("http");
  return (
    <a
      href={href}
      {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      style={{ color: TEAL, textDecoration: "underline", fontWeight: 600 }}
    >
      {children}
    </a>
  );
}

function PullQuote({ children }) {
  return (
    <div style={{ borderLeft: `3px solid ${TEAL}`, paddingLeft: 20, margin: "32px 0" }}>
      <p style={{ fontFamily: "var(--display)", fontSize: 20, fontWeight: 600, color: TEXT, lineHeight: 1.5, margin: 0 }}>
        {children}
      </p>
    </div>
  );
}

function InlineCTA({ children, href, label }) {
  return (
    <div
      style={{
        margin: "40px 0",
        padding: "24px 28px",
        borderRadius: 14,
        background: TEAL_DIM,
        border: "1px solid rgba(78,205,196,0.25)",
      }}
    >
      <p style={{ fontFamily: "var(--body)", fontSize: 15, color: TEXT, margin: "0 0 12px", fontWeight: 600 }}>
        {children}
      </p>
      <a
        href={href}
        style={{ color: TEAL, fontFamily: "var(--body)", fontSize: 15, fontWeight: 700, textDecoration: "none" }}
      >
        {label} &#8594;
      </a>
    </div>
  );
}

function Callout({ kind = "info", title, children }) {
  const palette =
    kind === "warn"
      ? { bg: "rgba(239,68,68,0.08)", border: "rgba(239,68,68,0.25)", titleColor: "#f87171" }
      : { bg: "rgba(167,139,250,0.08)", border: "rgba(167,139,250,0.25)", titleColor: "#a78bfa" };
  return (
    <div
      style={{
        margin: "32px 0",
        padding: "20px 24px",
        borderRadius: 12,
        background: palette.bg,
        border: `1px solid ${palette.border}`,
      }}
    >
      <div
        style={{
          fontFamily: "var(--display)",
          fontSize: 14,
          fontWeight: 700,
          color: palette.titleColor,
          marginBottom: 10,
          letterSpacing: "0.02em",
          textTransform: "uppercase",
        }}
      >
        {title}
      </div>
      <div>{children}</div>
    </div>
  );
}

function StepHeader({ id, num, title, time }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap", margin: "48px 0 16px" }}>
      <h2
        id={id}
        style={{
          fontFamily: "var(--display)",
          fontWeight: 700,
          fontSize: "clamp(22px, 3vw, 28px)",
          color: TEXT,
          margin: 0,
          letterSpacing: "-0.01em",
          lineHeight: 1.2,
          flex: "1 1 auto",
        }}
      >
        <span style={{ color: TEAL, fontFamily: "var(--mono)", fontSize: "0.75em", fontWeight: 700 }}>
          STEP {num}:
        </span>
        {" "}
        {title}
      </h2>
      <span
        aria-label={`Estimated time: ${time}`}
        style={{
          background: "rgba(255,255,255,0.04)",
          border: `1px solid ${CARD_BORDER}`,
          borderRadius: 999,
          padding: "4px 12px",
          fontFamily: "var(--mono)",
          fontSize: 12,
          color: TEXT_DIM,
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        {time}
      </span>
    </div>
  );
}

function MockupCTA() {
  return (
    <div
      style={{
        margin: "32px 0",
        padding: "20px 24px",
        borderRadius: 12,
        background: "rgba(255,255,255,0.03)",
        border: `1px solid ${CARD_BORDER}`,
      }}
    >
      <p style={{ fontFamily: "var(--body)", fontSize: 15, color: TEXT, margin: "0 0 8px", fontWeight: 600 }}>
        Visualize your ad before you build it
      </p>
      <p style={{ fontFamily: "var(--body)", fontSize: 14, color: TEXT_DIM, margin: "0 0 12px", lineHeight: 1.7 }}>
        Our free <A href="/tools/chatgpt-ads-mockup">ChatGPT Ads Mockup Generator</A> lets you type in your headline, description, and URL and see how your Sponsored Recommendation would appear, as you wrote it, inside a real ChatGPT conversation. Useful for client pitches, internal buy-in, or confirming your creative reads as a recommendation rather than a sales pitch. To compare it with the alternatives, our roundup of the <A href="/blog/best-chatgpt-ad-mockup-tools">best ChatGPT ad mockup tools</A> tests six free ones against OpenAI's specs.
      </p>
    </div>
  );
}


// ── HowTo schema step list ──
const howToSteps = [
  {
    name: "Pick your access path",
    text: "Choose between the self-serve Ads Manager (no account minimum since May 5, 2026 and a per-campaign minimum daily budget that varies by billing currency, $25/day in the US (September 2026), available to advertisers in more than 60 countries at ads.openai.com), Criteo's Commerce Media programmatic pipeline (existing-relationship pricing), or direct OpenAI sales (enterprise waitlist). Most B2B mid-market advertisers will use self-serve.",
  },
  {
    name: "Apply and verify your business",
    text: "Submit your application at openai.com/advertisers with your tax identification number, legal business name, and proof of business registration. Verification typically completes within 3 to 5 business days.",
  },
  {
    name: "Choose your campaign objective",
    text: "In the Ads Manager wizard, select Reach (CPM, impression-first; OpenAI's Help Center calls it Views), Clicks (CPC, traffic-first), or Conversions (optimizes toward a conversion event you pick and bills per click or per 1,000 impressions, not per conversion; it entered early access June 5, 2026 and needs the pixel or Conversions API, which launched broadly on May 5, 2026). Most first campaigns select Clicks.",
  },
  {
    name: "Set your starting max CPC bid",
    text: "Enter a maximum CPC bid for the ad group. OpenAI recommends starting a CPC campaign with a maximum bid of $3-$5 USD per click, and Ads Manager may show bid-strength guidance on whether a bid is likely to be competitive or may limit delivery. Treat $3-$5 as a starting point and adjust from your own click data. In the same wizard step, choose a daily or lifetime budget for the campaign.",
  },
  {
    name: "Write your Context hints",
    text: "Replace keyword targeting with plain-language descriptions of conversations where your ad should appear. Use persona-plus-intent, question-pattern, or topic-cluster-with-disqualifiers patterns. Keywords are accepted as a fallback but Context hints are the canonical primitive. Layer geo-targeting by US state, DMA, or ZIP code on top to limit delivery by region.",
  },
  {
    name: "Build conversational ad creative",
    text: "Write a 60-plus-word description that reads like a recommendation, not a promotional headline. Generate 3 to 5 creative variants per intent cluster covering feature-focused, benefit-focused, problem-focused, and comparison angles. An optional call-to-action button (Shop now, Book now, Sign up, Learn more) is in early testing on a subset of ads.",
  },
  {
    name: "Set up UTM tracking and GA4 conversion events",
    text: "Tag every landing page URL with a clean utm_source equals chatgpt convention, configure conversion events in Google Analytics 4, and connect to your CRM. The OpenAI conversion pixel and a server-side Conversions API launched broadly on May 5, 2026 (10 events, 30-day window); install one of them, and keep UTMs as the universal attribution layer that ties paid clicks to your CRM.",
  },
  {
    name: "Launch your campaign and validate organic AEO presence in parallel",
    text: "Submit creative for review (OpenAI publishes no standard review time) and monitor daily for the first two weeks. Run an organic AI visibility check at the same time so each paid Context hint cluster has a parallel organic AEO move ready to ship.",
  },
];

// ── FAQs ──
const faqs = [
  {
    q: "How do I get access to ChatGPT Ads in June 2026?",
    a: "Three paths. The self-serve Ads Manager opened to advertisers on April 10, 2026 and was opened to all US advertisers on May 5, 2026 with the account minimum removed; availability has since expanded to advertisers in more than 60 countries (September 2026). Sign up at ads.openai.com or apply at openai.com/advertisers. The Criteo Commerce Media pipeline (announced March 2, 2026) lets brands with existing Criteo relationships access ChatGPT inventory through their account. Direct OpenAI sales remains the enterprise-premium path with custom creative review and bespoke reporting.",
  },
  {
    q: "What is the minimum spend for a ChatGPT ad campaign?",
    a: "No account minimum since May 5, 2026, when OpenAI opened the self-serve Ads Manager to all US advertisers, though OpenAI documents a per-campaign minimum daily budget that varies by billing currency, $25/day in the US (September 2026). The minimum was $50,000 from April 13 to May 5, 2026 (the original $200,000 floor ran from February through early April). Self-serve campaign creation through the Ads Manager opened on April 10, 2026, and the dashboard wizard surfaced in late April.",
  },
  {
    q: "Can I set a daily budget for a ChatGPT ad campaign?",
    a: "Yes. As of the May 2026 Ads Manager update, each new campaign takes either a daily budget (an average across the Sunday-to-Saturday budget week: one day can spend up to twice it, the week never more than seven times it) or a lifetime budget (a total the campaign spends across its full flight). You choose one when you create the campaign, and the option is currently offered on newly created campaigns. With no account minimum since May 5, 2026 and a documented per-campaign minimum daily budget that varies by billing currency, $25/day in the US (September 2026), the budget you set above that floor is what shapes the test, so size it to the click volume you expect at your maximum CPC bid.",
  },
  {
    q: "Can I target ChatGPT ads by location?",
    a: "Yes. The Ads Manager added geo-targeting in May 2026: you can target by US state, DMA (designated market area), or ZIP code, set during campaign creation or edited on a running campaign. Geo-targeting works alongside Context hints, not instead of them. Context hints match the conversation; geo-targeting limits delivery to the regions you select. For B2B it is most useful for field-sales territories, event-driven campaigns, or excluding regions you do not serve.",
  },
  {
    q: "Do ChatGPT ads have call-to-action buttons?",
    a: "Partly, and only in an early test as of late May 2026. OpenAI began testing an optional call-to-action button on a small subset of ads (on the Free and Go tiers), the first direct-response element inside a ChatGPT ad unit. The button text is auto-selected from your creative and destination from a fixed set: Shop now, Book now, Sign up, and Learn more. Advertisers cannot manually choose the button yet, though OpenAI has said manual control may come later. Treat it as emerging rather than a standard field every campaign has.",
  },
  {
    q: "How long does it take to set up a ChatGPT ad campaign end-to-end?",
    a: "Roughly 4 hours of active work, plus creative review, for which OpenAI publishes no standard turnaround. The 4-hour estimate breaks down as: 15 minutes to pick an access path, 20 to 30 minutes for business verification, 5 minutes inside the wizard for objective and budget, 10 minutes for max CPC, 30 to 45 minutes to write Context hints, 60 to 90 minutes for creative, and 30 minutes to wire UTMs and GA4 conversion events. From application to live ads, expect day 3 to 5 in calendar time.",
  },
  {
    q: "What is a Context hint and how is it different from a keyword?",
    a: "A Context hint is a plain-language description of the conversations where your ad should appear, which OpenAI converts into a vector embedding and matches against the embedding of each live ChatGPT conversation. Keywords are exact-match strings; Context hints are semantic descriptions. Keywords are still accepted as a fallback input but the platform treats them as one possible source of hint text rather than the canonical primitive.",
  },
  {
    q: "When can I use the Conversions objective in the wizard?",
    a: "Conversion-optimized campaigns entered early access on June 5, 2026, for accounts that set up the OpenAI Pixel or Conversions API by June 1; those dates come from OpenAI's communications to advertisers, as reported by Digiday and Search Engine Land, not a public OpenAI announcement. The Conversions objective is selectable in the Ads Manager as of August 2026, and it asks you to pick the conversion event to optimize for (the conversion pixel and a server-side Conversions API launched May 5, 2026, tracking 10 events with a 30-day attribution window, backed by the April 30, 2026 privacy policy). Views (CPM, shown as Reach in Ads Manager) and Clicks (CPC) remain available alongside it. Despite the name, Conversions bills per click or per 1,000 impressions, not per conversion.",
  },
  {
    q: "How do I track conversions beyond what the pixel reports?",
    a: "UTM parameters on every landing page URL plus GA4 conversion events plus CRM enrichment. The OpenAI conversion pixel and a server-side Conversions API launched broadly on May 5, 2026 and track 10 events with a 30-day attribution window; for anything past those 10 events or that window, lean on UTMs. Tag URLs with a clean utm_source equals chatgpt utm_medium equals cpc convention, define your conversion events in Google Analytics 4, push UTMs into your CRM with the lead record, and use multi-touch attribution rather than last-click. AI-Advisors stitches organic AI referrals, paid AI ad visits, and Google Search Console traffic into a single time-series so you can see all three channels on one chart.",
  },
  {
    q: "Can I move my Google Ads campaigns directly to ChatGPT Ads?",
    a: "No. The platforms are fundamentally different. Google Ads matches keywords; ChatGPT Ads match conversation embeddings. Google Ads creative is short and punchy; ChatGPT Ads creative is 60-plus words and reads like a recommendation. Google Ads targets demographics and audiences; ChatGPT Ads targets via Context hints, with a maximum bid set at the ad-group level. Reusing Google copy verbatim produces ads that look out of place inside a conversation and get ignored.",
  },
  {
    q: "Should I run paid ChatGPT Ads and organic AEO at the same time?",
    a: "Yes. The same prompt research that earns organic citations targets paid placements. Every Context hint cluster you launch should have a parallel AEO move: an AEO-optimized landing page targeting the same prompt cluster, a direct-answer paragraph structured for retrieval, and a tracked weekly prompt run on Perplexity, ChatGPT Search, and Google AI Overviews. Paid earns the placement. Organic earns the citation. Smart B2B teams run both because the work in one channel makes the other work harder.",
  },
];

function Content() {
  return (
    <article>
      {/* ── Featured-snippet box ── */}
      <div
        style={{
          margin: "0 0 32px",
          padding: "20px 24px",
          borderRadius: 12,
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.07)",
        }}
      >
        <P>
          <Strong>
            To set up a ChatGPT ad campaign, pick an access path (self-serve at ads.openai.com, with no account minimum and a per-campaign daily minimum that varies by billing currency, US $25/day; Criteo; or direct), then run the Ads Manager wizard: objective, budget and bid, Context hints, geo-targeting, creative, tracking, and launch. Expect about 4 hours of active work plus creative review, for which OpenAI publishes no standard turnaround.
          </Strong>
        </P>
        <ul style={{ paddingLeft: 20, margin: "0 0 0" }}>
          <Li><Strong>Three access paths:</Strong> self-serve Ads Manager (April 10, 2026), Criteo Commerce Media pipeline, or direct OpenAI sales</Li>
          <Li><Strong>Minimum spend:</Strong> No account minimum since May 5, 2026; OpenAI documents a per-campaign daily minimum that varies by billing currency (US $25/day, September 2026). The account minimum was $50K from April 13 to May 5 and $200K at launch</Li>
          <Li><Strong>Targeting:</Strong> Context hints (plain-language descriptions, not keywords), plus geo-targeting by state, DMA, or ZIP</Li>
          <Li><Strong>Budget and bidding:</Strong> a daily or lifetime budget, plus a manual max CPC bid (OpenAI recommends starting a CPC campaign with a maximum bid of $3-$5 USD per click). Check this field rather than accepting it: new ad groups can default to an automated strategy that carries <A href="/blog/chatgpt-ads-maximize-results-b2b">no cost-efficiency guarantee</A></Li>
          <Li><Strong>Setup time:</Strong> ~4 hours active work, plus creative review before launch (OpenAI publishes no standard review time)</Li>
        </ul>
      </div>

      {/* ── Animated mockup: here is what you are about to build ── */}
      <P style={{ marginBottom: 8 }}>Here is what you are about to build.</P>
      <ChatGPTAdMockup />

      {/* ── Intro ── */}
      <P>
        <A href="/glossary/chatgpt-ads">ChatGPT Ads</A> went from invite-only enterprise pilot to fully open self-serve in 86 days. OpenAI <A href="https://openai.com/index/testing-ads-in-chatgpt/">launched ad testing</A> on February 9, 2026, dropped the minimum from $200,000 to $50,000 on April 13, opened the wizard in the wild in late April, and on May 5, 2026 <A href="/blog/chatgpt-ads-opens-to-all-us-advertisers">opened the Ads Manager to every US advertiser</A> and removed the minimum entirely. In the weeks since, OpenAI layered on <A href="/blog/chatgpt-ads-budgets-geo-targeting-cta-buttons">daily and lifetime budgets, geo-targeting, and the first call-to-action buttons</A> (the week of May 21, 2026), and conversion-optimized campaigns entered early access on June 5, 2026, with the Conversions objective now selectable in the Ads Manager. Availability has also expanded beyond the US, to advertisers in more than 60 countries. The platform is no longer behind a sales-team gate.
      </P>
      <P>
        This guide walks the entire setup, end to end, against what is actually in the dashboard today. Eight steps, ~4 hours of active work, with 5 visuals, an interactive budget estimator, and a closing analytics view that shows what your tracking is actually for. If you need background on <A href="/blog/chatgpt-ads-complete-guide">what ChatGPT Ads are</A>, or on <A href="/blog/best-chatgpt-ads-management-platforms">which platform to manage campaigns in</A>, those companion posts go deeper. This one assumes you are ready to build.
      </P>

      <PullQuote>
        Setting up a ChatGPT ad campaign is no longer the hard part. Setting up a ChatGPT ad campaign that pairs with a working AEO foundation is.
      </PullQuote>

      {/* ── Visual 1: Setup flow diagram ── */}
      <div
        style={{
          background: "#0f0f11",
          borderRadius: 12,
          border: `1px solid ${CARD_BORDER}`,
          padding: 24,
          margin: "16px 0 32px",
          overflow: "hidden",
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 4, fontFamily: "var(--body)" }}>
          ChatGPT Ads Setup Flow
        </div>
        <div style={{ fontSize: 10, color: TEXT_DIM, marginBottom: 16, fontFamily: "var(--body)" }}>
          8 steps. ~4 hours active work. Plus creative review before launch.
        </div>
        <div className="hide-scrollbar" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", margin: "0 -4px" }}>
          <svg
            viewBox="0 0 760 200"
            xmlns="http://www.w3.org/2000/svg"
            style={{ width: "100%", minWidth: 680, height: "auto", display: "block" }}
            role="img"
            aria-label="Horizontal flow diagram with 8 numbered steps connected by arrows: Pick access path, Apply and verify, Choose objective, Set max CPC, Write Context hints, Build creative, Set up tracking, Launch and run AEO parallel."
          >
            {[
              { n: 1, x: 20, label: "Pick", l2: "access path" },
              { n: 2, x: 110, label: "Apply +", l2: "verify" },
              { n: 3, x: 200, label: "Choose", l2: "objective" },
              { n: 4, x: 290, label: "Set max", l2: "CPC" },
              { n: 5, x: 380, label: "Write", l2: "Context hints" },
              { n: 6, x: 470, label: "Build", l2: "creative" },
              { n: 7, x: 560, label: "Set up", l2: "tracking" },
              { n: 8, x: 650, label: "Launch +", l2: "AEO parallel" },
            ].map((s, i, arr) => (
              <g key={s.n}>
                {i > 0 && (
                  <line
                    x1={arr[i - 1].x + 88}
                    y1="80"
                    x2={s.x + 2}
                    y2="80"
                    stroke="rgba(79,205,196,0.4)"
                    strokeWidth="1.5"
                    markerEnd="url(#arrow)"
                  />
                )}
                <rect
                  x={s.x}
                  y="40"
                  width="84"
                  height="80"
                  rx="10"
                  ry="10"
                  fill={s.n === 8 ? "rgba(79,205,196,0.18)" : "rgba(79,205,196,0.06)"}
                  stroke={s.n === 8 ? TEAL : "rgba(79,205,196,0.3)"}
                  strokeWidth={s.n === 8 ? "2" : "1"}
                />
                <text x={s.x + 42} y="62" textAnchor="middle" fontSize="11" fill={TEAL} fontFamily="var(--mono)" fontWeight="700">
                  STEP {s.n}
                </text>
                <text x={s.x + 42} y="83" textAnchor="middle" fontSize="12" fill={TEXT} fontFamily="var(--body)" fontWeight="600">
                  {s.label}
                </text>
                <text x={s.x + 42} y="100" textAnchor="middle" fontSize="11" fill={TEXT_DIM} fontFamily="var(--body)">
                  {s.l2}
                </text>
              </g>
            ))}
            <defs>
              <marker id="arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                <polygon points="0 0, 6 3, 0 6" fill={TEAL} />
              </marker>
            </defs>
            {/* AI Visibility Lift callout under Step 8 */}
            <text x="692" y="145" textAnchor="middle" fontSize="9" fill={TEAL} fontFamily="var(--body)" fontWeight="700">
              The AI Visibility Lift
            </text>
            <text x="692" y="158" textAnchor="middle" fontSize="9" fill={TEXT_DIM} fontFamily="var(--body)">
              lives in Step 8
            </text>
          </svg>
        </div>
      </div>

      {/* ── STEP 1: Access path ── */}
      <StepHeader id="step-1" num="1" title="Pick your access path" time="~15 min" />
      <P>
        Three paths into the platform as of June 2026. The right one depends on your budget, your existing ad-tech relationships, and how much hand-holding you need.
      </P>

      <H3>Self-serve Ads Manager (most B2B advertisers)</H3>
      <P>
        The <A href="/blog/chatgpt-ads-opens-to-all-us-advertisers">self-serve Ads Manager</A> launched April 10, 2026 to advertisers in early access, then <A href="/blog/chatgpt-ads-opens-to-all-us-advertisers">opened to every US advertiser</A> on May 5, 2026 with no account minimum (was $50K from April 13). Sign up at <a href="https://ads.openai.com/" target="_blank" rel="noopener noreferrer" style={{ color: TEAL, textDecoration: "underline", fontWeight: 600 }}>ads.openai.com</a> or apply at <a href="https://openai.com/advertisers/" target="_blank" rel="noopener noreferrer" style={{ color: TEAL, textDecoration: "underline", fontWeight: 600 }}>openai.com/advertisers</a>. Our <A href="/blog/how-to-use-chatgpt-ads-manager">marketer&#39;s guide to the Ads Manager UI</A> covers the dashboard structure, the 7-step submit-for-review setup, the 4-vertical eligibility constraint, and the pixel-vs-Conversions-API choice that B2B SaaS attribution depends on. The dashboard surfaces impressions, clicks, and pixel-tracked conversions in real time and is functionally similar to Google Ads. The <A href="/blog/chatgpt-ads-conversion-tracking">conversion tracking pixel</A> and a server-side Conversions API launched broadly on May 5, 2026 (10 events, 30-day window); frequency capping remains in development.
      </P>

      <H3>Criteo Commerce Media pipeline</H3>
      <P>
        Criteo was named OpenAI&#39;s first ad tech partner on March 2, 2026 and expanded the GO platform to SMBs in the US and UK on March 31. If your brand already runs Criteo campaigns, you can access ChatGPT inventory through your existing programmatic relationship. In April 2026, one buyer reported CPMs of $25 to $35 through Criteo; OpenAI publishes no CPM rate. Often the fastest path to inventory if you have a Criteo account already.
      </P>

      <H3>Direct OpenAI sales</H3>
      <P>
        The original pilot access path, still available for enterprise brands with direct OpenAI relationships. Custom creative review, bespoke reporting, managed-service overhead. OpenAI publishes no CPM rate for this route and points larger advertisers with more complex needs to its Ads team for sales support.
      </P>

      <H3>Decision tree</H3>

      {/* ── Visual 2: Access path decision tree ── */}
      <div
        style={{
          background: "#0f0f11",
          borderRadius: 12,
          border: `1px solid ${CARD_BORDER}`,
          padding: 24,
          margin: "16px 0 32px",
          overflow: "hidden",
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 16, fontFamily: "var(--body)" }}>
          Which access path is right for you?
        </div>
        <div className="hide-scrollbar" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", margin: "0 -4px" }}>
          <svg
            viewBox="0 0 720 260"
            xmlns="http://www.w3.org/2000/svg"
            style={{ width: "100%", minWidth: 600, height: "auto", display: "block" }}
            role="img"
            aria-label="Four-column access path matrix. Column 1: if you have a Criteo Commerce Media account, buy through your existing Criteo pipeline. Column 2: if you have no organic AEO presence, run AEO first to build the foundation and test small ($1K-$5K). Column 3: with a $5K-$100K test budget, choose the self-serve Ads Manager, where most B2B advertisers go. Column 4: if you need sales support, contact OpenAI's Ads team."
          >
            <defs>
              <marker id="dt-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                <polygon points="0 0, 6 3, 0 6" fill="rgba(79,205,196,0.6)" />
              </marker>
            </defs>

            {[
              {
                x: 16,
                crit: "Have a Criteo account",
                title: "Criteo Commerce",
                titleSub: "Media",
                desc1: "Existing pipeline.",
                desc2: "No new account.",
                style: "teal-dim",
              },
              {
                x: 192,
                crit: "No organic AEO presence",
                title: "Run AEO first",
                titleSub: "",
                desc1: "Build the foundation,",
                desc2: "test small ($1K-$5K).",
                style: "red",
              },
              {
                x: 368,
                crit: "$5K-$100K test budget",
                title: "Self-serve",
                titleSub: "Ads Manager",
                desc1: "Most B2B",
                desc2: "advertisers go here.",
                style: "teal-bright",
              },
              {
                x: 544,
                crit: "Needs sales support",
                title: "Direct OpenAI",
                titleSub: "",
                desc1: "Contact OpenAI's",
                desc2: "Ads team.",
                style: "teal-dim",
              },
            ].map((col, i) => {
              const fill =
                col.style === "red"
                  ? "rgba(239,68,68,0.1)"
                  : col.style === "teal-bright"
                  ? "rgba(79,205,196,0.18)"
                  : "rgba(79,205,196,0.08)";
              const stroke =
                col.style === "red"
                  ? "rgba(239,68,68,0.4)"
                  : col.style === "teal-bright"
                  ? TEAL
                  : "rgba(79,205,196,0.4)";
              const titleFill = col.style === "red" ? "#f87171" : TEAL;
              const strokeWidth = col.style === "teal-bright" ? "2" : "1.5";
              const cx = col.x + 80;
              return (
                <g key={i}>
                  {/* Criterion box */}
                  <rect
                    x={col.x}
                    y={20}
                    width={160}
                    height={56}
                    rx="8"
                    ry="8"
                    fill="rgba(255,255,255,0.04)"
                    stroke="rgba(255,255,255,0.15)"
                  />
                  <text x={cx} y={42} textAnchor="middle" fontSize="10" fill={TEXT_DIM} fontFamily="var(--mono)" fontWeight="600">
                    IF
                  </text>
                  <text x={cx} y={62} textAnchor="middle" fontSize="12" fill={TEXT} fontFamily="var(--body)" fontWeight="700">
                    {col.crit}
                  </text>

                  {/* Connector arrow */}
                  <line
                    x1={cx}
                    y1={86}
                    x2={cx}
                    y2={108}
                    stroke="rgba(79,205,196,0.5)"
                    strokeWidth="1.5"
                    markerEnd="url(#dt-arrow)"
                  />

                  {/* Outcome card */}
                  <rect
                    x={col.x}
                    y={120}
                    width={160}
                    height={104}
                    rx="10"
                    ry="10"
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                  />
                  <text x={cx} y={148} textAnchor="middle" fontSize="14" fill={titleFill} fontFamily="var(--body)" fontWeight="700">
                    {col.title}
                  </text>
                  {col.titleSub && (
                    <text x={cx} y={166} textAnchor="middle" fontSize="14" fill={titleFill} fontFamily="var(--body)" fontWeight="700">
                      {col.titleSub}
                    </text>
                  )}
                  <text x={cx} y={col.titleSub ? 192 : 180} textAnchor="middle" fontSize="11" fill={TEXT_DIM} fontFamily="var(--body)">
                    {col.desc1}
                  </text>
                  <text x={cx} y={col.titleSub ? 208 : 196} textAnchor="middle" fontSize="11" fill={TEXT_DIM} fontFamily="var(--body)">
                    {col.desc2}
                  </text>
                </g>
              );
            })}

            {/* Footnote */}
            <text x={360} y={246} textAnchor="middle" fontSize="10" fill={TEXT_DIM} fontFamily="var(--body)" fontStyle="italic">
              Have a Criteo account? Pick Criteo first for its existing pipeline (one buyer saw $25-$35 CPMs in April 2026). No account minimum on any path.
            </text>
          </svg>
        </div>
      </div>

      {/* ── STEP 2: Apply and verify ── */}
      <StepHeader id="step-2" num="2" title="Apply and verify your business" time="~20-30 min" />
      <P>
        Whichever path you picked, you need to verify your business before any campaign goes live. OpenAI runs stricter advertiser verification than Google or Meta. Have these ready before you start the application.
      </P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>Tax identification number.</Strong> EIN for US businesses, equivalent for international. Must match your legal business name exactly.</Li>
        <Li><Strong>Legal business name.</Strong> As it appears on tax documents, not your DBA or marketing name.</Li>
        <Li><Strong>Proof of business registration.</Strong> Articles of incorporation, business license, or country equivalent.</Li>
        <Li><Strong>Billing entity details.</Strong> Bank routing for ACH or a corporate card sized for your planned monthly burn (no account minimum since May 5, 2026; OpenAI documents a per-campaign daily minimum that varies by billing currency, US $25/day).</Li>
        <Li><Strong>Authorized advertiser contact.</Strong> Someone whose role can sign the advertising terms.</Li>
      </ul>
      <P>
        Submit through openai.com/advertisers. Verification typically completes within 3 to 5 business days. Most rejections trace back to tax-name mismatches; double-check the legal name before you submit.
      </P>

      {/* ── STEP 3: Objective ── */}
      <StepHeader id="step-3" num="3" title="Choose your campaign objective" time="~5 min in wizard" />
      <P>
        Once verified, the Ads Manager opens to a 3-step wizard. Step 1 is Create Campaign. Step 2 is Create Ad Group and Ads. Step 3 is Review. The hierarchy mirrors Google Ads: campaigns hold ad groups, ad groups hold ads.
      </P>
      <P>
        The Objective dropdown in Step 1 reveals three options. <Strong>Reach</Strong> (OpenAI&#39;s Help Center calls it Views) for impression-first CPM campaigns. <Strong>Clicks</Strong> for CPC campaigns. <Strong>Conversions</Strong>, which optimizes toward a conversion event but bills per click or per 1,000 impressions, not per conversion. Views and Clicks have been live since launch; Conversions entered early access on June 5, 2026 and is selectable in the Ads Manager today, where it asks you to pick the conversion event to optimize for and how you are billed. Our <A href="/blog/how-to-set-up-chatgpt-ads-conversion-campaigns">B2B guide to Conversions campaigns</A> covers both choices.
      </P>

      {/* ── Visual 3: Campaign wizard mockup (REUSED) ── */}
      <ChatGPTAdsCampaignWizardMockup />

      <P>
        Most first campaigns select <Strong>Clicks</Strong>. CPC pricing makes the math intuitive (you set the most you will pay per click), OpenAI documents a recommended starting bid up front, and the data you collect against UTM-tagged landing pages is more actionable than impression-only Views data while OpenAI&#39;s measurement layer is still maturing.
      </P>
      <ScreenshotFrame
        src="/screenshots/ai-ads/ai-advisors-chatgpt-ads-conversions-campaign-countries.png"
        title="AI Ads / New campaign"
        alt="AI-Advisors campaign wizard with the ChatGPT Ads Conversions objective, Lead created as the optimization event, and Brazil, the UK and Mexico selected."
        caption="The Conversions objective with an Optimize-for conversion event selected."
      />

      <Callout kind="info" title="The Conversions objective is live">
        <P>
          The Conversions objective shipped in the UI before OpenAI connected the wizard's bidder, and that bidder is now connected. The pixel itself and a server-side Conversions API launched broadly on May 5, 2026 as a self-serve beta (they track 10 events, with a 30-day attribution window), backed by the April 30, 2026 privacy policy that formalized the data-flow disclosures. OpenAI&#39;s <A href="/blog/chatgpt-ads-conversion-tracking#bidding-evolution">conversion-optimized campaigns</A> entered early access on June 5, 2026 for accounts that set up the pixel or Conversions API by June 1 (per OpenAI&#39;s communications to advertisers, as reported by Digiday and Search Engine Land); OpenAI now also documents third-party measurement-partner integrations. Selecting Conversions now asks you to pick the conversion event to optimize for, so install the pixel or Conversions API first and keep measuring conversions downstream in GA4 plus your CRM (Step 7 covers this), or see our <A href="/blog/chatgpt-ads-conversion-tracking">deep-dive on ChatGPT Ads conversion tracking</A> for the full pixel + roadmap context.
        </P>
      </Callout>

      {/* ── STEP 4: Bid ── */}
      <StepHeader id="step-4" num="4" title="Set your starting max CPC bid" time="~10 min" />
      <P>
        Step 2 of the wizard surfaces a Maximum CPC bid limit field. OpenAI&#39;s <A href="https://help.openai.com/en/articles/20001207-ads-in-chatgpt-the-basics">Help Center</A> says advertisers set a maximum bid at the ad-group level, and for CPC campaigns it recommends starting with a maximum bid of $3-$5 USD per click. Ads Manager may also show bid-strength guidance on whether a bid is likely to be competitive or may limit delivery. Early screenshots, posted by <A href="https://x.com/glenngabe/status/2046566221055734110">Glenn Gabe on April 21, 2026</A> (images shared by Juozas Kaziukenas) and reported by <A href="https://www.seroundtable.com/chatgpt-ad-manager-interface-41195.html">Search Engine Roundtable</A>, showed the Ads Manager telling advertisers to enter a max bid between $3.00 and $5.00. Since August 2026, Maximize results has been the default bid strategy for eligible new ad groups; it sets and adjusts bids automatically with no guarantee of a CPA, CPC, or ROAS target, and Manual: Max bid is the opt-in route to a hard ceiling.
      </P>
      <P>
        The $3-$5 figure is a starting bid, not a price list. OpenAI says it has no performance benchmarks across advertisers, industries, or campaign types, so what clicks actually cost is something you learn from your own campaigns; <A href="https://www.searchenginejournal.com/6-months-into-chatgpt-ads-advertisers-still-dont-know-what-good-looks-like/587583/">Search Engine Journal&#39;s September 2026 report</A> quoted advertiser-reported blended CPCs of $4.41, $9.29, and $9.89. The cost-modeling implication: stress-test your budget at more than one CPC. At a $3 to $5 CPC, a $50,000 budget buys about 10,000 to 16,700 clicks; at $9.89 it buys roughly 5,000.
      </P>
      <P>
        For the cost-impact framing across $50,000, see <A href="/blog/chatgpt-ads-cost">how much do ChatGPT Ads cost</A>.
      </P>

      <PullQuote>
        Treat OpenAI&#39;s recommended starting bid as a place to begin, then let your own click data set your maximum bid.
      </PullQuote>

      {/* ── Budget Estimator (between Step 4 and Step 5) ── */}
      <H2 id="budget-estimator">Estimate your monthly spend</H2>
      <P>
        Budget is the variable that ripples through every other step: it sets your click volume, your daily burn, and your test cadence. With no account minimum on the self-serve dashboard since May 5, 2026, every business now has access to the same path; OpenAI documents a per-campaign minimum daily budget that varies by billing currency, $25/day in the US (September 2026), so budget shapes the test rather than gating it. Move the sliders below to see what your spend buys at different CPC and CTR assumptions.
      </P>
      <P>
        You set this budget directly in the wizard. As of the May 2026 Ads Manager update, each new campaign takes either a <Strong>daily budget</Strong> (a seven-day average; one day can spend up to twice it) or a <Strong>lifetime budget</Strong> (a total the campaign spends across its full flight). The choice is currently offered on newly created campaigns. Pair it with the max CPC from Step 4 and the estimate below to size your first test.
      </P>

      <ChatGPTAdsBudgetEstimator />

      {/* ── STEP 5: Context hints ── */}
      <StepHeader id="step-5" num="5" title="Write your Context hints" time="~30-45 min" />
      <P>
        This is where ChatGPT Ads diverge most from every other ad platform. Google asks for keywords. Meta asks for interests. LinkedIn asks for job titles. ChatGPT asks: <Strong>describe the conversations where your ad should appear.</Strong> The field is labeled &quot;Context hints&quot; and the helper text reads: <em>&quot;Context hints provide more plain-language guidance on when your ad should appear. Describe the type of queries your ad will be most relevant to, or paste a set of keywords if you have them.&quot;</em>
      </P>

      {/* ── Visual 4: Context hints mockup (REUSED) ── */}
      <ChatGPTAdsContextHintsMockup />

      <H3>How Context hints actually work under the hood</H3>
      <P>
        OpenAI converts your hint into vector space. Each live ChatGPT conversation is also embedded. Cosine similarity decides whether your ad fits. There is no &quot;exact match&quot; or &quot;phrase match&quot; toggle, because there is no string-matching layer. Keywords are accepted as a fallback input; they get converted to embeddings just like any other string. The framing in the helper text tells you which paradigm OpenAI thinks is canonical.
      </P>

      <H3>Three writing patterns that work</H3>
      <ol style={{ paddingLeft: 20, margin: "0 0 18px", color: TEXT_DIM }}>
        <Li>
          <Strong>Persona plus intent.</Strong> &quot;Marketing leaders comparing CRM platforms for a 50-person remote team.&quot; Names the audience and the decision stage in one phrase.
        </Li>
        <Li>
          <Strong>Question patterns.</Strong> &quot;Users asking how to set up multi-channel attribution for a B2B SaaS funnel.&quot; Mirrors the natural shape of a ChatGPT prompt.
        </Li>
        <Li>
          <Strong>Topic clusters with disqualifiers.</Strong> &quot;CRM evaluation, not implementation help; mid-market not enterprise; sales-led, not product-led.&quot; Narrows the moment the hint describes. It does not block adjacent conversations: OpenAI says hints cannot enforce exclusions.
        </Li>
      </ol>

      <H3>Where the hints come from</H3>
      <P>
        The same prompt research that earns organic citations on Perplexity, ChatGPT Search, and Microsoft Copilot is the same input that targets paid ads on ChatGPT. If you already know which prompts cite you (or fail to cite you) for your category, you already have your context hints. The <A href="/blog/how-chatgpt-recommends">organic recommendation pipeline</A> and the paid Context hints field consume the same research. Most B2B teams duplicate the work because they treat paid and organic as separate functions; they do not have to.
      </P>

      <H3>Layer geo-targeting on top of Context hints</H3>
      <P>
        Context hints decide which conversations your ad fits; geo-targeting decides where the person having them sits. As of the May 2026 update, the wizard targets by <Strong>US state, DMA</Strong> (designated market area), <Strong>or ZIP code</Strong>, set at campaign creation or edited later on a running campaign. For B2B this matters most for field-sales territories, event-driven pushes, and excluding regions you cannot service. Leave it national if your buyer is location-agnostic; the Context hints are still doing the heavy targeting.
      </P>

      <PullQuote>
        Context hints turn prompt research into a paid-targeting primitive. The same prompt research that earns organic citations targets paid placements. One input, two channels.
      </PullQuote>

      <InlineCTA href="/tools/google-ads-to-chatgpt-ads-converter" label="Run the free Converter">
        Skip the 30 to 45 minutes Step 5 takes from scratch. The free Converter takes up to 10 of your Google Ads keywords with no sign-in and returns Context hints (each classified into 1 of 5 Patterns), ChatGPT-ready ad copy, and a convertibility read. Connect Google Ads and it converts your live Search campaigns instead.
      </InlineCTA>

      {/* ── STEP 6: Creative ── */}
      <StepHeader id="step-6" num="6" title="Build conversational ad creative" time="~60-90 min" />
      <P>
        ChatGPT users are reading, thinking, and problem-solving. They are not scanning a search results page looking for the best blue link. Your ad appears below an AI-generated answer, inside a conversation. It needs to feel like it belongs there.
      </P>
      <P>
        <Strong>The formula:</Strong> empathize with the question, deliver a specific answer, introduce your solution, and offer a low-friction next step. Each piece should feel like a natural continuation of the conversation above it.
      </P>
      <P>The difference in practice:</P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>Traditional ad copy:</Strong> &quot;Best Project Management Software. Try Free for 14 Days. Trusted by 50,000+ Teams.&quot;</Li>
        <Li><Strong><A href="/glossary/conversational-ad">Conversational ad</A> copy:</Strong> &quot;If you&#39;re comparing project management tools for a remote team, Acme PM was built specifically for async workflows. Teams typically cut missed deadlines by 40% in the first month. See how it handles your exact use case with a free workspace.&quot;</Li>
      </ul>
      <P>
        The second version works because it sounds like a recommendation from someone who understood the question. Early data shows ads with 60-plus words of explanatory content get 2 to 3 times higher engagement than short promotional messages. Users in ChatGPT expect detail.
      </P>
      <P>
        Write at least 3 to 5 creative variants per intent cluster. Test different angles: feature-focused, benefit-focused, problem-focused, and competitor-comparison. You will not know what resonates until you have real data, and you will not know what data you have until you tag every variant with a unique UTM (Step 7).
      </P>
      <P>
        One newer creative element to watch: OpenAI is running an <Strong>early test</Strong> of a call-to-action button on a small subset of ads (on the Free and Go tiers), the first direct-response element inside the ad unit itself. The button text is auto-selected from your creative and destination, drawn from a fixed set of &quot;Shop now,&quot; &quot;Book now,&quot; &quot;Sign up,&quot; and &quot;Learn more.&quot; You cannot hand-pick it yet, so the lever you control is the creative and landing page it is generated from. Write those so the implied next action is unmistakable.
      </P>

      <MockupCTA />

      {/* ── STEP 7: Tracking ── */}
      <StepHeader id="step-7" num="7" title="Set up UTM tracking and GA4 conversion events" time="~30 min" />
      <P>
        Measurement is still maturing on this platform. The OpenAI conversion pixel and a server-side Conversions API launched broadly on May 5, 2026 as a self-serve beta (they track 10 events, with a 30-day attribution window), backed by the April 30, 2026 privacy policy that formalized the data flows. <A href="/blog/how-to-set-up-chatgpt-ads-custom-audiences">Custom audiences</A> for uploaded-list retargeting then shipped on May 14, 2026 in gated rollout. Lookalike audiences are not documented, though OpenAI now documents third-party measurement-partner integrations. ChatGPT reports impressions, clicks, and pixel or Conversions API events, with 7-, 14- or 30-day click windows and an optional one-day view-through window; everything beyond that (longer view-through, multi-touch attribution, closed-deal revenue) is on you to track via UTMs and your own analytics stack. To install the pixel, see our <A href="/blog/how-to-install-chatgpt-ads-pixel-in-google-tag-manager">GTM install walkthrough</A> for the 5-step Custom HTML setup, then confirm the Pixel ID and events with our free <A href="/tools/chatgpt-ads-pixel-checker">ChatGPT Ads pixel checker</A>.
      </P>
      <P>Build a measurement stack that survives the gap:</P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li>
          <Strong>UTM convention.</Strong> Every ad variation needs a unique UTM string. Standardize on <code style={{ background: "rgba(255,255,255,0.06)", padding: "2px 6px", borderRadius: 4, fontFamily: "var(--mono)", fontSize: 13, color: TEXT }}>?utm_source=chatgpt&amp;utm_medium=cpc&amp;utm_campaign=&#123;cluster&#125;&amp;utm_content=&#123;variant&#125;</code>. Document the convention in a shared doc so anyone tagging URLs uses the same fields. Rather than hand-tagging every variant, you can set <code style={{ background: "rgba(255,255,255,0.06)", padding: "2px 6px", borderRadius: 4, fontFamily: "var(--mono)", fontSize: 13, color: TEXT }}>utm_content=&#123;ad_id&#125;</code> once under Landing page query parameters at the campaign level; Ads Manager fills in each ad&#39;s ID at delivery, and anything already written into an ad&#39;s own URL takes precedence. Our <A href="/blog/how-to-add-utm-codes-to-chatgpt-ads">UTM guide for ChatGPT Ads</A> walks through it.
        </Li>
        <Li>
          <Strong>GA4 conversion events.</Strong> Define your conversion events (form fills, demo requests, trial signups) explicitly in GA4. Build a custom report filtered to <code style={{ background: "rgba(255,255,255,0.06)", padding: "2px 6px", borderRadius: 4, fontFamily: "var(--mono)", fontSize: 13, color: TEXT }}>utm_source=chatgpt</code> to isolate performance.
        </Li>
        <Li>
          <Strong>CRM enrichment.</Strong> Push UTM data into your CRM with the lead record. This lets you track each click through the full sales cycle and calculate actual ROI, not just click cost.
        </Li>
        <Li>
          <Strong>Multi-touch attribution.</Strong> ChatGPT often sits early in the buyer journey; someone researches in ChatGPT, then comes back via Google or direct. Last-click attribution will undervalue this channel every time. Use a position-based or time-decay model.
        </Li>
      </ul>

      {/* ── Analytics & Reporting section (NEW) ── */}
      <H2 id="analytics-reporting">See organic and paid ChatGPT traffic in one dashboard</H2>
      <P>
        UTMs and GA4 events answer half the question. They tell you what each channel did separately. They do not tell you whether the new Context hint cluster also lifted organic citations, or whether the campaign creative matched the citation style of pages already ranking. For that, you need organic and paid stitched into a single time-series.
      </P>
      <P>
        AI-Advisors stitches the AI citation feed (organic crawls plus referrals) and the UTM-tagged paid clicks into the same Overview dashboard you already use for Google Search Console, direct, and the rest of your channel mix. Now you can answer the only question that matters for the AI Visibility Lift: did the same audience find you in organic ChatGPT recommendations as in paid Sponsored Recommendations, and at what ratio?
      </P>

      <AIAnalyticsOverviewMockup />

      <P>
        The pre-launch period above shows a Blaze CRM advertiser already earning organic ChatGPT referrals (the AEO foundation Step 8 will validate) alongside steady Google Search and Direct traffic. April 15 is the moment Step 8 puts the Context hints from Step 5 into a live campaign. Paid ChatGPT drives the visible volume after launch, but organic ChatGPT also accelerates because the same Context hints are validated as AEO targets. <A href="/platform/ai-analytics">See AI Analytics in detail</A> for how the channel attribution model is structured.
      </P>

      {/* ── STEP 8: Launch + AEO parallel ── */}
      <StepHeader id="step-8" num="8" title="Launch your campaign and validate organic AEO presence in parallel" time="~ongoing" />
      <P>
        Submit through Step 3 of the wizard. OpenAI does not publish a standard review time, so leave a buffer before any launch date that matters. OpenAI reviews creative against advertising policies (no health claims, no financial guarantees, no political messaging, no targeting under 18). If rejected, you receive specific feedback on what to fix; tighten the copy and resubmit.
      </P>
      <P>
        Once approved, ads go live. Monitor impressions, clicks, and CTR daily for the first two weeks. Advertisers who published results in September 2026 reported click-through rates of 0.7% to 0.94% (<A href="https://www.searchenginejournal.com/6-months-into-chatgpt-ads-advertisers-still-dont-know-what-good-looks-like/587583/">Search Engine Journal</A>), though OpenAI publishes no benchmark and results vary by category. Do not panic if your first week looks slow; conversational ads take time to optimize. No impressions at all after a full day is a different problem, and our <A href="/blog/chatgpt-ads-no-impressions">no-impressions checklist</A> walks through what can block delivery.
      </P>
      <ScreenshotFrame
        src="/screenshots/ai-ads/ai-advisors-chatgpt-ads-overview-dashboard.png"
        title="AI Ads / Overview"
        alt="AI-Advisors AI Ads overview for ChatGPT Ads, with impressions, clicks, CTR, spend, average CPC and CPM tiles above a daily performance chart."
        caption="Impressions, clicks, CTR, and spend on one screen, with a daily performance chart."
      />

      <H3>The AEO parallel for every Context hint cluster</H3>
      <P>
        The single biggest mistake B2B teams make on launch day is treating paid as a standalone channel. It is not. Every Context hint cluster you launch should ship with a parallel AEO move. Same prompt research, two outputs.
      </P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>Per paid creative variant:</Strong> ship one AEO-optimized landing page targeting the same prompt cluster.</Li>
        <Li><Strong>Per max CPC bid set:</Strong> ship one direct-answer paragraph structured for retrieval (<A href="/glossary/faqpage-schema">FAQ schema</A>, 60-word direct answer, citation-friendly tone).</Li>
        <Li><Strong>Per UTM-tagged URL:</Strong> ship one tracked weekly prompt run on Perplexity, ChatGPT Search, and <A href="/blog/how-to-get-cited-by-google-ai-overviews">Google AI Overviews</A> to measure organic citation share.</Li>
        <Li><Strong>Per creative review approval:</Strong> ship one BlogPosting + Person + Organization JSON-LD audit on the matching landing page.</Li>
        <Li><Strong>Per launch day:</Strong> ship one IndexNow ping for any new content so Bing-stack engines (Microsoft Copilot, ChatGPT Search) crawl within minutes.</Li>
        <Li><Strong>Per daily monitoring routine:</Strong> ship one weekly prompt-set re-run to track citation share trend over time.</Li>
      </ul>
      <P>
        The <A href="/blog/how-to-get-recommended-by-chatgpt">organic recommendation playbook</A> covers the AEO side step-by-step. The <A href="/blog/5-as-ai-marketing-framework">5 A&#39;s framework</A> is the higher-order operating model that holds paid and organic together. Both link back to the same prompt research that drove your Context hints in Step 5.
      </P>

      <PullQuote>
        Paid earns the placement. Organic earns the citation. Smart B2B teams ship both, because the same prompt research drives both.
      </PullQuote>

      {/* ── Time recap ── */}
      <H2 id="time-recap">How long does each step take?</H2>
      <P>About 4 hours of active work, plus creative review before live impressions start. OpenAI publishes no standard review time.</P>
      <div className="hide-scrollbar" style={{ overflowX: "auto", margin: "16px 0 32px" }}>
        <table style={{ width: "100%", minWidth: 480, borderCollapse: "collapse", fontFamily: "var(--body)", fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${CARD_BORDER}` }}>
              <th style={{ textAlign: "left", padding: "10px 12px", color: TEXT_DIM, fontWeight: 600 }}>Step</th>
              <th style={{ textAlign: "left", padding: "10px 12px", color: TEXT_DIM, fontWeight: 600 }}>What</th>
              <th style={{ textAlign: "right", padding: "10px 12px", color: TEXT_DIM, fontWeight: 600, whiteSpace: "nowrap" }}>Time</th>
            </tr>
          </thead>
          <tbody>
            {[
              [1, "Pick your access path", "~15 min"],
              [2, "Apply and verify your business", "~20-30 min"],
              [3, "Choose your campaign objective", "~5 min"],
              [4, "Set your starting max CPC bid", "~10 min"],
              [5, "Write your Context hints", "~30-45 min"],
              [6, "Build conversational ad creative", "~60-90 min"],
              [7, "Set up UTM tracking and GA4 events", "~30 min"],
              [8, "Launch and run AEO parallel", "Ongoing"],
            ].map(([n, w, t]) => (
              <tr key={n} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                <td style={{ padding: "10px 12px", color: TEAL, fontFamily: "var(--mono)", fontWeight: 700, whiteSpace: "nowrap" }}>STEP {n}</td>
                <td style={{ padding: "10px 12px", color: TEXT }}>{w}</td>
                <td style={{ padding: "10px 12px", color: TEXT_DIM, fontFamily: "var(--mono)", textAlign: "right", whiteSpace: "nowrap" }}>{t}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Small budget / not ready to spend ── */}
      <H2 id="small-budget">What if you only have $1K-$5K to test, or you're not ready to spend yet?</H2>
      <P>
        With the May 5 account-minimum removal, every business based in any of the more than 60 available markets can now test ChatGPT Ads, subject to the per-campaign minimum daily budget that varies by billing currency, $25/day in the US OpenAI documents as of September 2026. A $1K-$5K test reads directional signal: enough impressions and clicks to learn which Context hint clusters and creative angles resonate, even if it's not enough to settle a last-click ROAS debate. That said, many teams will want to prepare now and run campaigns later, or skip paid entirely in favor of organic visibility. All three are valid postures.
      </P>
      <P>
        <Strong>Start with organic visibility.</Strong> Businesses that show up in ChatGPT&#39;s organic responses today get the most out of paid placements tomorrow. Run our <A href="/tools/ai-visibility-checker">free AI Visibility check</A> to see where you stand. If ChatGPT isn&#39;t recommending you yet, <A href="/blog/how-to-get-recommended-by-chatgpt">learn how to get recommended by ChatGPT first</A>. Organic presence is the multiplier that makes the paid layer work harder once you do buy.
      </P>
      <P>
        <Strong>Build your conversational creative now.</Strong> Start developing ad messaging that works in a conversational format. Use our <A href="/tools/chatgpt-ads-mockup">ChatGPT Ads Mockup Generator</A> to visualize how your ads would look inside a real conversation. Test different angles, get feedback from your team, and have polished creative ready to go when you do launch.
      </P>
      <P>
        <Strong>Stand up your tracking infrastructure.</Strong> UTM conventions, GA4 conversion events, CRM enrichment: all of this can be configured before you spend a dollar on ads. When you launch, you will be collecting clean data from day one instead of scrambling to set up measurement after your budget is already running.
      </P>
      <P>
        <Strong>Apply ChatGPT to your existing Google Ads work.</Strong> Most teams reach for ChatGPT only after they&#39;ve decided to launch a ChatGPT Ads campaign. The <A href="/blog/chatgpt-for-google-ads">upstream use case</A> is using ChatGPT as a copilot for the Google Ads work you already do today: writing responsive search ad copy, mining negative keywords from your search-terms report, and running an AI Visibility audit on top keywords to flag which ones are most exposed to displacement. The 90-minute weekly review cadence works whether or not you ever launch a ChatGPT Ads test.
      </P>

      <PullQuote>
        The account minimum is gone. The right question is no longer "can you afford it" but "is your AEO foundation ready to compound the paid signal." Build the foundation first, test small, then scale.
      </PullQuote>

      <InlineCTA href="/playbook" label="Open the 5 A's Playbook">
        The Playbook walks through the citation audit, prompt research, and content production loop that produces both your AEO wins and the Context hints for paid.
      </InlineCTA>

      {/* ── HowTo + FAQPage JSON-LD ── */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How to Set Up a ChatGPT Ad Campaign in 8 Steps",
            description:
              "Complete step-by-step guide to setting up a ChatGPT ad campaign in June 2026 (no account minimum since May 5, 2026): access path, application, objective, budget and bid, Context hints, geo-targeting, creative, tracking, and launch with parallel organic AEO.",
            totalTime: "PT4H",
            estimatedCost: { "@type": "MonetaryAmount", currency: "USD", value: "5000" },
            step: howToSteps.map((s, i) => ({
              "@type": "HowToStep",
              position: i + 1,
              name: s.name,
              text: s.text,
              url: `https://www.ai-advisors.ai/blog/how-to-set-up-chatgpt-ad-campaign#step-${i + 1}`,
            })),
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          }),
        }}
      />

      {/* ── FAQ section ── */}
      <H2 id="faq">Frequently Asked Questions</H2>
      <FAQItems faqs={faqs} />

      {/* ── Related reading ── */}
      <H2 id="related">Related Reading</H2>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><A href="/blog/how-to-optimize-landing-pages-for-chatgpt-ads">How to Optimize Landing Pages for ChatGPT Ads</A> (the destination-page playbook for the campaigns you set up)</Li>
        <Li><A href="/blog/chatgpt-ads-opens-to-all-us-advertisers">ChatGPT Ads Just Opened to Every US Advertiser: What Marketers Should Do First</A></Li>
        <Li><A href="/blog/best-chatgpt-ads-management-platforms">Best ChatGPT Ads Management Platforms in 2026</A> (if you would rather manage the campaign outside OpenAI&rsquo;s dashboard)</Li>
        <Li><A href="/blog/chatgpt-ads-cost">How Much Do ChatGPT Ads Cost? CPM Range, CPC Bids (October 2026 Update)</A></Li>
        <Li><A href="/blog/chatgpt-ads-complete-guide">ChatGPT Advertising for Business: Is It Worth It in 2026?</A></Li>
        <Li><A href="/blog/how-to-get-recommended-by-chatgpt">How to Get Recommended by ChatGPT: The Organic Counterpart</A></Li>
        <Li><A href="/blog/how-to-convert-google-ads-to-chatgpt-ads-in-the-ai-advisors-platform">How to Convert Google Ads to ChatGPT Ads in the AI-Advisors Platform</A></Li>
      </ul>
    </article>
  );
}

const post = {
  slug: "how-to-set-up-chatgpt-ad-campaign",
  title: "How to Set Up a ChatGPT Ad Campaign in 8 Steps (September 2026)",
  seoTitle: "How to Set Up a ChatGPT Ad Campaign (Sept 2026)",
  excerpt:
    "An 8-step guide to setting up a ChatGPT ad campaign in June 2026 (no account minimum since May 5, 2026), with the actual Ads Manager wizard, the recommended starting CPC bid, daily and lifetime budgets, state and ZIP geo-targeting, the Context hints field, an interactive budget estimator, a unified organic + paid traffic chart, and the AI Visibility Lift that makes paid work harder.",
  metaDescription:
    "Set up a ChatGPT ad campaign in 8 steps with the June 2026 Ads Manager: no account minimum, Context hints, a starting CPC bid, budgets, and geo-targeting.",
  date: "2026-03-10",
  lastUpdated: "2026-11-01",
  readTime: "15 min read",
  category: "AI Ads",
  tags: ["ChatGPT Ads", "Campaign Setup", "HowTo", "Context hints", "AI Advertising", "AEO"],
  Content,
};

export default post;
